import React, { FC } from 'react'
import { addDisplayName, flowMax, addProps, addDefaultProps } from 'ad-hok'
import cx from 'classnames'
import { createUseStyles } from 'react-jss'

import lasso1 from 'assets/products-lasso.svg'
import lasso2 from 'assets/creators-lasso.svg'
import roundupLasso from 'assets/roundup-lasso.svg'
import { serifFontFamily } from '../utils/styles'

type Layout = 'rodeo1' | 'rodeo2' | 'roundup'
type AnimationDirection = 'normal' | 'reverse' | undefined

interface StyleProps {
  width?: string | number
  animate: boolean
  animationDirection: AnimationDirection
  animationDuration: string | number
  animationTimingFunction?: string
  maxLassoHeight?: string | number
}

const animationName =
  (name: string) =>
  ({ animate }: StyleProps) =>
    animate ? name : undefined

const animationDirection = ({ animationDirection }: StyleProps) =>
  animationDirection

const animationDuration = ({ animationDuration }: StyleProps) =>
  animationDuration

const animationTimingFunction = ({ animationTimingFunction }: StyleProps) =>
  animationTimingFunction

const useStyles = createUseStyles({
  absoluteCenter: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rodeoContainer: {
    paddingTop: 20,
    display: 'flex',
    justifyContent: 'center',
    opacity: '100%',
    animationName: animationName('$rodeoContainerIn'),
    animationDirection,
    animationDuration,
    animationTimingFunction,
    pointerEvents: 'none',
  },
  '@keyframes rodeoContainerIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: '100%',
    },
  },
  childrenContainer: {
    animationName: animationName('$childrenContainerIn'),
    animationDirection,
    animationDuration,
    animationTimingFunction,
  },
  '@keyframes childrenContainerIn': {
    from: {
      transform: 'scale(6)',
    },
    to: {
      transform: 'scale(1)',
    },
  },
  lassoContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    maxHeight: ({ maxLassoHeight }: StyleProps) => maxLassoHeight,
  },
  lasso: {
    width: ({ width }: StyleProps) => width && '90%',
  },
  lassoFlipped: {
    transform: 'scaleX(-1)',
  },
  child: {
    position: 'absolute',
    pointerEvents: 'auto',
  },
  title: {
    fontFamily: serifFontFamily,
    fontSize: 24,
    fontWeight: 700,
    color: 'white',
    textTransform: 'lowercase',
    marginTop: 20,
  },
})

interface Props extends Partial<StyleProps> {
  layout: Layout
  title?: string
  childClasses: string[]
  isFlipped?: boolean
  id?: string
  className?: string
  maxLassoHeight?: string
  button?: JSX.Element
}

export const Lasso: FC<Props> = flowMax(
  addDisplayName('Lasso'),
  addDefaultProps({
    isFlipped: false,
  }),
  addProps(
    ({
      width,
      animate,
      animationDirection,
      animationDuration,
      animationTimingFunction,
      maxLassoHeight,
    }) => ({
      classes: useStyles({
        width,
        animate: animate ?? false,
        animationDirection,
        animationDuration: animationDuration ?? '0.4s',
        animationTimingFunction,
        maxLassoHeight,
      }),
    })
  ),
  addProps(({ layout }) => ({
    image:
      layout === 'rodeo1'
        ? lasso1
        : layout === 'rodeo2'
        ? lasso2
        : roundupLasso,
  })),
  ({
    image,
    title,
    children,
    id,
    isFlipped,
    button,
    childClasses,
    classes,
    className,
  }) => (
    <div id={id} className={cx(classes.rodeoContainer, className)}>
      <div className={classes.lassoContainer}>
        <img
          src={image}
          alt=""
          className={cx(classes.lasso, isFlipped && classes.lassoFlipped)}
        />
        <div className={cx(classes.absoluteCenter, classes.childrenContainer)}>
          {React.Children.toArray(children).map((child, index) => (
            <div className={cx(classes.child, childClasses[index])} key={index}>
              {child}
            </div>
          ))}
          {title && <div className={classes.title}>{title}</div>}
        </div>
        {button}
      </div>
    </div>
  )
)
